/**
 * One list over two document types.
 *
 * The portal shows goods DeCAs and passenger route sheets in a single list
 * (design D1), but they are served by two independently paginated endpoints.
 * The merge is exact rather than approximate for one reason: **both sources
 * order by `createdAt`** — see the `orderBy` built from `filters.sort` in
 * `decaRepository`'s and `passengerDecaRepository`'s `list()` in
 * `repositories.ts` — so merging on that key reproduces the true order.
 *
 * What it cannot do is page arbitrarily deep. To render page N the merge may
 * need N × perPage rows from *either* source — all of page N could come from one
 * side — and the goods list caps `perPage` at 100 (the `Math.min(100, …)` clamp
 * in the `/api/v1/decas` GET handler in `decas.ts`). That bound is computed and
 * reported here rather than assumed at the call site, because it is arithmetic
 * between two constants that live apart and know nothing of each other:
 * `DECA_PER_PAGE` in the portal, and that clamp in the goods route. Raising the
 * page size for cosmetic reasons silently shortens the reachable depth, and
 * `reachablePages` is what makes that fail loudly.
 *
 * A second failure mode lives beside that one and is not the same: a caller
 * that simply fetched too few rows for a page it otherwise could have served.
 * `assertFetchedEnough` below is what makes that fail loudly too — see there.
 */

import type { DecaSummary, PassengerDecaSummary } from '@deca/api'

/** The smaller of the two servers' per-page caps. Goods is the binding one. */
export const MERGE_FETCH_CAP = 100

export type DocumentRow =
  | { tipo: 'mercancias'; row: DecaSummary }
  | { tipo: 'viajeros'; row: PassengerDecaSummary }

export interface MergeInput {
  goods: { rows: readonly DecaSummary[]; total: number }
  passenger: { rows: readonly PassengerDecaSummary[]; total: number }
  page: number
  perPage: number
}

export interface MergeResult {
  rows: DocumentRow[]
  /** Exact: the sum of two exact totals, regardless of how deep paging reaches. */
  total: number
  totalPages: number
  /** The last page a capped fetch can serve correctly. */
  reachablePages: number
  /**
   * True when **this request** asked for a page beyond `reachablePages`.
   *
   * ⚠️ Not the question a screen wants when it is deciding whether to tell the
   * user the list is cut short. A pager clamped to `reachablePages` — which is
   * what `DecaListScreen` renders — can never produce such a request, so this
   * stays false however many documents are out of reach, and the notice reads as
   * dead code that fires only for a hand-edited URL. The collection-level
   * question is `totalPages > reachablePages`, and that is what the screen gates
   * its notice on; see the comment there.
   *
   * Kept because it is the honest answer to what it asks, and a caller that wants
   * to send an out-of-range page back to page one needs exactly this.
   */
  truncated: boolean
  /**
   * Rows the caller must request from each source to serve this page — or, once
   * that would exceed `MERGE_FETCH_CAP`, the most a source will ever hand back
   * in one request. Past `reachablePages` this is a ceiling on what is
   * obtainable, not a guarantee that fetching it makes the page correct.
   */
  requiredPerSource: number
}

export function mergeDocumentPages(input: MergeInput): MergeResult {
  const { goods, passenger, page, perPage } = input

  // Computed first: both the guard below and the returned field need it, and
  // it must not exceed what a source will ever hand back in one request.
  const requiredPerSource = Math.min(page * perPage, MERGE_FETCH_CAP)
  assertFetchedEnough('goods', goods.rows.length, goods.total, requiredPerSource)
  assertFetchedEnough('passenger', passenger.rows.length, passenger.total, requiredPerSource)

  const all: DocumentRow[] = [
    ...goods.rows.map((row): DocumentRow => ({ tipo: 'mercancias', row })),
    ...passenger.rows.map((row): DocumentRow => ({ tipo: 'viajeros', row })),
  ]

  // `id` breaks a tie, so two documents created in the same millisecond do not
  // swap places between renders. Source order would be an arbitrary tiebreak
  // that changes the moment either endpoint's page boundary moves.
  all.sort((a, b) => {
    const byDate = b.row.createdAt.localeCompare(a.row.createdAt)
    return byDate !== 0 ? byDate : a.row.id.localeCompare(b.row.id)
  })

  const total = goods.total + passenger.total
  // Unclamped on purpose: once `perPage` exceeds `MERGE_FETCH_CAP`, even page one
  // cannot be guaranteed correct (it could need up to `perPage` rows from one
  // source, more than a single request will ever return), and `0` is what
  // reports that honestly. A floor of 1 here would instead tell the caller page
  // one is fine while quietly handing it half the rows it needs — silent in
  // exactly the region this field exists to make loud.
  const reachablePages = Math.floor(MERGE_FETCH_CAP / perPage)
  const start = (page - 1) * perPage

  return {
    rows: all.slice(start, start + perPage),
    total,
    totalPages: Math.max(1, Math.ceil(total / perPage)),
    reachablePages,
    truncated: page > reachablePages,
    requiredPerSource,
  }
}

/**
 * Guards against a caller that fetched fewer rows than this page needs from one
 * source — a different failure from `truncated`. A deep page is *honestly*
 * unreachable given the fixed server caps; an under-fetch is a caller mistake —
 * the source has enough rows (`total` says so) and simply was not asked for
 * enough of them. Left unchecked, that produces a page that looks authoritative
 * — sorted, sliced, a confident `total` — while quietly missing rows.
 *
 * These are legally significant compliance documents, so this throws rather
 * than folding the shortfall into `truncated`: `truncated` is a signal a screen
 * is expected to render around (design D1's "deepest page we can show"), which
 * would let a caller bug ship as a quiet, permanent UI state instead of the
 * loud, immediate failure a precondition violation should be. The comparison is
 * against `Math.min(requiredPerSource, total)`, not `requiredPerSource` alone,
 * so a source that genuinely has fewer documents than a full page never trips
 * this — only fetching less than what actually exists does.
 */
function assertFetchedEnough(
  source: 'goods' | 'passenger',
  rowCount: number,
  total: number,
  requiredPerSource: number,
): void {
  const need = Math.min(requiredPerSource, total)
  if (rowCount < need) {
    throw new Error(
      `mergeDocumentPages: ${source} under-fetched — got ${rowCount} row(s) but this page needs ${need} ` +
        `(requiredPerSource ${requiredPerSource}, total ${total}). Fetch at least ${need} rows from ` +
        `${source} before merging.`,
    )
  }
}
